import React, { useState, useEffect, useRef, useMemo } from 'react';
import ProjectCard from './ProjectCard';
import projects from '../data/projectsData';

const STATUSES = ['All', 'In Progress', 'Completed', 'Not Started'];
const SORT_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'status-asc', label: 'Status: Not Started → Completed' },
  { value: 'status-desc', label: 'Status: Completed → Not Started' },
  { value: 'title-asc', label: 'Title: A → Z' },
  { value: 'title-desc', label: 'Title: Z → A' },
];

const STATUS_ORDER = { 'Not Started': 0, 'In Progress': 1, 'Completed': 2 };


function ProjectsGrid() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [sortBy, setSortBy] = useState('default');
  const sectionRefs = useRef([]);

  // Re-run reveal observer when filter/sort changes
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    // Small delay to let DOM update
    const timer = setTimeout(() => {
      sectionRefs.current.forEach((el) => el && observer.observe(el));
    }, 50);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [activeFilter, sortBy]);

  const filteredAndSorted = useMemo(() => {
    let result = activeFilter === 'All'
      ? [...projects]
      : projects.filter((p) => p.status === activeFilter);

    switch (sortBy) {
      case 'status-asc':
        result.sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
        break;
      case 'status-desc':
        result.sort((a, b) => STATUS_ORDER[b.status] - STATUS_ORDER[a.status]);
        break;
      case 'title-asc':
        result.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'title-desc':
        result.sort((a, b) => b.title.localeCompare(a.title));
        break;
      default:
        break;
    }

    return result;
  }, [activeFilter, sortBy]);

  // Count per status for the filter badges
  const counts = useMemo(() => {
    const c = { All: projects.length };
    STATUSES.forEach((s) => {
      if (s !== 'All') c[s] = projects.filter((p) => p.status === s).length;
    });
    return c;
  }, []);

  return (
    <section className="projects-section">
      <div className="section-inner">
        {/* Filter & Sort controls */}
        <div className="projects-controls" role="toolbar" aria-label="Filter and sort projects">
          <div className="projects-filters" role="group" aria-label="Filter by status">
            {STATUSES.map((status) => (
              <button
                key={status}
                type="button"
                className={`projects-filter-btn ${activeFilter === status ? 'active' : ''}`}
                onClick={() => setActiveFilter(status)}
                aria-pressed={activeFilter === status}
              >
                {status}
                <span className="projects-filter-count">{counts[status]}</span>
              </button>
            ))}
          </div>
          <div className="projects-sort">
            <label htmlFor="project-sort" className="projects-sort-label">
              Sort by
            </label>
            <select
              id="project-sort"
              className="projects-sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Results count */}
        <p className="projects-results-count" aria-live="polite">
          Showing {filteredAndSorted.length} of {projects.length} projects
        </p>

        {/* Grid */}
        <div className="projects-grid">
          {filteredAndSorted.length === 0 ? (
            <p className="projects-empty">No projects match the selected filter.</p>
          ) : (
            filteredAndSorted.map((project, i) => (
              <div
                key={project.id}
                ref={(el) => (sectionRefs.current[i] = el)}
                className="reveal-wrap"
              >
                <ProjectCard project={project} animationDelay={i * 0.08} />
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}

export default ProjectsGrid;
